import type { QueryClient } from '@tanstack/react-query';
import type {
  ClientSupportMessage,
  ClientSupportMessagesResponse,
  ClientSupportThreadsResponse,
} from './types';

export function chatMessagesQueryKey(userId: number) {
  return ['client-support-chat', 'messages', userId] as const;
}

export const CHAT_THREADS_QUERY_KEY = ['client-support-chat', 'threads'] as const;
export const CHAT_UNREAD_QUERY_KEY = ['client-support-chat', 'unread-count'] as const;

let activeAdminChatUserId: number | null = null;

export function setActiveAdminChatUserId(userId: number | null) {
  activeAdminChatUserId = userId;
}

export function getActiveAdminChatUserId() {
  return activeAdminChatUserId;
}

export function mergeClientSupportMessages(
  prev: ClientSupportMessagesResponse | undefined,
  next: ClientSupportMessagesResponse,
): ClientSupportMessagesResponse {
  if (!prev?.items.length) return next;
  const byId = new Map(prev.items.map((item) => [item.id, item]));
  for (const item of next.items) byId.set(item.id, item);
  const items = [...byId.values()].sort((a, b) => {
    const byTime = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return byTime !== 0 ? byTime : a.id - b.id;
  });
  return {
    threadId: next.threadId || prev.threadId,
    items,
    total: Math.max(next.total, items.length),
  };
}

function countsAsAdminUnread(message: ClientSupportMessage): boolean {
  return (
    message.senderKind === 'CLIENT' ||
    message.senderKind === 'SELLER' ||
    message.systemEvent === 'ORDER_PROBLEM'
  );
}

/** Поднять тред в начало списка диалогов при новом сообщении. */
function bumpClientThreadInList(
  queryClient: QueryClient,
  userId: number,
  message: ClientSupportMessage,
  meta?: { userName?: string | null; userPhone?: string | null },
) {
  queryClient.setQueryData<ClientSupportThreadsResponse>(CHAT_THREADS_QUERY_KEY, (prev) => {
    if (!prev) return prev;
    const existing = prev.items.find((item) => item.userId === userId);
    const isActive = activeAdminChatUserId === userId;
    const unreadDelta = !isActive && countsAsAdminUnread(message) ? 1 : 0;
    const thread = existing
      ? {
          ...existing,
          lastMessage: message,
          updatedAt: message.createdAt,
          unreadCount: existing.unreadCount + unreadDelta,
          hasOpenOrderProblem:
            message.systemEvent === 'ORDER_PROBLEM'
              ? true
              : message.systemEvent === 'ORDER_PROBLEM_RESOLVED'
                ? false
                : existing.hasOpenOrderProblem,
        }
      : {
          threadId: message.threadId,
          userId,
          userName: meta?.userName ?? null,
          userPhone: meta?.userPhone ?? null,
          lastMessage: message,
          unreadCount: unreadDelta,
          updatedAt: message.createdAt,
          hasOpenOrderProblem: message.systemEvent === 'ORDER_PROBLEM',
        };
    const without = prev.items.filter((item) => item.userId !== userId);
    return {
      ...prev,
      items: [thread, ...without],
      total: existing ? prev.total : prev.total + 1,
    };
  });
}

export function appendClientSupportMessageToCache(
  queryClient: QueryClient,
  userId: number,
  message: ClientSupportMessage,
  meta?: { userName?: string | null; userPhone?: string | null },
) {
  queryClient.setQueryData<ClientSupportMessagesResponse>(
    chatMessagesQueryKey(userId),
    (prev) => {
      const base = prev ?? { items: [], total: 0, threadId: message.threadId };
      if (base.items.some((item) => item.id === message.id)) return base;
      return {
        ...base,
        threadId: message.threadId || base.threadId,
        items: [...base.items, message],
        total: base.total + 1,
      };
    },
  );
  bumpClientThreadInList(queryClient, userId, message, meta);
  void queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
}
